import { animated, config, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import type { CSSProperties, ReactElement } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { getRarityColor } from '@sorare/core/src/lib/colors';

const Wrapper = styled.div`
  position: relative;
  aspect-ratio: var(--card-aspect-ratio);
`;

const FrontWrapper = styled(animated.div)`
  position: absolute;
  inset: 0;
  /* offset of 1px for the animated card stroke */
  clip-path: inset(-1px -1px calc(100% - 1px - var(--reveal-percent)) -1px);
`;

const Mask = styled(animated.div)`
  opacity: 0;
`;

const BackWrapper = styled(animated.div)`
  position: absolute;
  inset: 0;
  clip-path: inset(calc(var(--reveal-percent) + 1px) -1px -1px -1px);
`;

const LightningBar = styled(animated.div)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: -10%;
  right: 10%;
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: var(--color);
    height: 16px;
    width: 120%;
    z-index: 1;
    border-radius: 50%;
    filter: blur(4px);
  }
  &.hidden {
    display: none;
  }
`;

export const PickedCardRevealer = ({
  front,
  back,
  onRest,
  rarity,
  revealed,
  immediate,
}: {
  front: ReactElement;
  back: ReactElement;
  onRest?: () => void;
  rarity: Rarity;
  revealed: boolean;
  immediate: boolean;
}) => {
  const [animationFinished, setAnimationFinished] = useState(false);
  const style = useSpring({
    from: {
      y: '0%',
    },
    to: {
      y: '100%',
    },
    config: config.molasses,
    pause: !revealed,
    immediate,
    onRest: () => {
      setAnimationFinished(true);
      onRest?.();
    },
  });

  return (
    <Wrapper
      style={
        {
          '--color': getRarityColor(rarity),
        } as CSSProperties
      }
    >
      <Mask>{back}</Mask>
      <BackWrapper style={{ '--reveal-percent': style.y } as CSSProperties}>
        {back}
      </BackWrapper>

      <FrontWrapper style={{ '--reveal-percent': style.y } as CSSProperties}>
        {front}
      </FrontWrapper>
      <LightningBar
        style={style}
        className={classNames({
          hidden: !revealed || animationFinished,
        })}
      />
    </Wrapper>
  );
};
